import { useEffect, useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { API } from '@unboared/react';

import { GAMES } from '~/config/global.config';
import { useGames } from '~/hooks/models/useGame';

import styles from './FreePlayIdleScreen.module.css';

const ROTATE_MS = 14000;
const LANG_MS = 7000;

type LangContent = {
    label: string;
    title1: string;
    title2: string;
    lead: string;
    steps: [string, string, string];
    cta: string;
    /** The two objections that stop a passer-by from scanning at all: "will this
        cost me?" and "do I have to install something?". Shown next to the QR in
        every displayed language — a traveller decides here, before scanning. */
    free: string;
    showing: string;
    count: (n: number) => string;
};

/** Attract-screen copy per language. Add an entry (e.g. `zh`) to support a new
    airport — the displayed pair is chosen by the `languages` prop, no redesign. */
const CONTENT: Record<string, LangContent> = {
    fr: {
        label: 'FR',
        title1: 'Votre jeu,',
        title2: 'votre choix.',
        lead: 'Scannez, choisissez, jouez ensemble sur le grand écran.',
        steps: ['Scannez', 'Choisissez', 'Jouez'],
        cta: 'Scannez pour jouer',
        free: 'Gratuit · Sans application',
        showing: 'À l’affiche',
        count: (n) => `${n} jeux disponibles`,
    },
    en: {
        label: 'EN',
        title1: 'Your game,',
        title2: 'your choice.',
        lead: 'Scan, choose and play together on the big screen.',
        steps: ['Scan', 'Choose', 'Play'],
        cta: 'Scan to play',
        free: 'Free · No app needed',
        showing: 'Now showing',
        count: (n) => `${n} games available`,
    },
    zh: {
        label: '中文',
        title1: '你的游戏，',
        title2: '你来选。',
        lead: '扫码、选择，一起在大屏幕上玩。',
        steps: ['扫码', '选择', '开玩'],
        cta: '扫码开玩',
        free: '免费 · 无需下载应用',
        showing: '正在展示',
        count: (n) => `${n} 款游戏`,
    },
};

/** Anim/bar variant: here the ANIMATOR picks the game, the player just joins and
    waits — so the copy is join-oriented, not "choose your game" (auto/airport). */
const CONTENT_ANIM: Record<string, LangContent> = {
    fr: {
        label: 'FR',
        title1: 'L’animateur',
        title2: 'prépare la partie.',
        lead: 'Scannez le QR code pour rejoindre — ça démarre dans un instant.',
        steps: ['Scannez', 'Rejoignez', 'Jouez'],
        cta: 'Scannez pour rejoindre',
        free: 'Gratuit · Sans application',
        showing: 'À l’affiche',
        count: (n) => `${n} jeux au programme`,
    },
    en: {
        label: 'EN',
        title1: 'The host',
        title2: 'is setting up.',
        lead: 'Scan the QR code to join — the game starts in a moment.',
        steps: ['Scan', 'Join', 'Play'],
        cta: 'Scan to join',
        free: 'Free · No app needed',
        showing: 'Now showing',
        count: (n) => `${n} games on the menu`,
    },
    zh: {
        label: '中文',
        title1: '主持人',
        title2: '正在准备游戏。',
        lead: '扫码加入 — 马上开始。',
        steps: ['扫码', '加入', '开玩'],
        cta: '扫码加入',
        free: '免费 · 无需下载应用',
        showing: '正在展示',
        count: (n) => `${n} 款游戏`,
    },
};

/**
 * Free-play attract / idle screen — shown on the TV while no game has been picked.
 *
 * Renders NO real game (so the next picked game starts fresh) and embeds the real
 * session QR. Background = the catalogue's game trailers (muted, looping) with the
 * game name shown. Bilingual by design: the editorial copy cycles between the
 * client's two languages while the QR call-to-action stays shown in BOTH at once,
 * so a traveller never misses how to join. Validated 2026-06-18.
 *
 * `languages` is the display pair (default FR/EN) — set it per client to localise
 * (e.g. ['en', 'zh']) without touching this component.
 */
export function FreePlayIdleScreen({
    gameIDs = [],
    languages = ['fr', 'en'],
    variant = 'auto',
}: {
    gameIDs?: string[];
    languages?: string[];
    /** 'auto' = aéroport/free-play (le joueur choisit) · 'anim' = bar (l'animateur
        choisit, le joueur rejoint). Pilote la copie éditoriale affichée. */
    variant?: 'auto' | 'anim';
}) {
    const dict = variant === 'anim' ? CONTENT_ANIM : CONTENT;

    // `languages` identity is unstable BY CONSTRUCTION: the default parameter above
    // allocates a fresh ['fr','en'] on every render, and callers may pass a literal.
    // So we normalise to a primitive key first and rebuild a stable array from it —
    // every hook below can then depend on `requested` honestly, and the warn effect
    // fires once per configuration change instead of once per render (the component
    // re-renders on its own 7s/14s timers, which would otherwise flood Sentry).
    // Array.isArray is also the belt-and-braces to the converter's guard: this value
    // ultimately comes from a hand-typed Firestore field, and calling .filter on a
    // string would blank the whole TV app via the top-level ErrorBoundary.
    const langKey = (Array.isArray(languages) ? languages : []).join(',');
    const requested = useMemo(() => (langKey ? langKey.split(',') : []), [langKey]);

    const langs = useMemo(() => requested.filter((l) => dict[l]), [requested, dict]);
    const pair = langs.length > 0 ? langs : ['fr', 'en'];

    // A typo in the Firestore field (wrong case, unsupported code) otherwise fails
    // SILENTLY — the screen just reverts to FR/EN and the only way to notice is to
    // walk up to the TV. Warn so it lands in Sentry breadcrumbs instead.
    useEffect(() => {
        const dropped = requested.filter((l) => !dict[l]);
        if (dropped.length > 0) {
            // eslint-disable-next-line no-console
            console.warn(
                `[FreePlayIdleScreen] langue(s) ignorée(s): ${dropped.join(', ')}. Supportées: ${Object.keys(dict).join(', ')}.`
            );
        }
    }, [requested, dict]);

    const list = useMemo(() => (gameIDs.length > 0 ? gameIDs : GAMES), [gameIDs]);
    const games = useGames(list);

    const showcase = useMemo(
        () =>
            (games ?? [])
                .map((g) => ({ name: g.name, video: g.video, poster: g.cover || g.icon }))
                .filter((g) => g.video || g.poster),
        [games]
    );

    const [index, setIndex] = useState(0);
    useEffect(() => {
        if (showcase.length < 2) return undefined;
        const id = window.setInterval(() => setIndex((i) => (i + 1) % showcase.length), ROTATE_MS);
        return () => window.clearInterval(id);
    }, [showcase.length]);

    // Trailers whose URL failed (404 / unsupported codec) — fall back to the cover
    // instead of sitting on a black/frozen video for the slot.
    const [failedVideos, setFailedVideos] = useState<Record<string, true>>({});

    const [langIdx, setLangIdx] = useState(0);
    useEffect(() => {
        if (pair.length < 2) return undefined;
        const id = window.setInterval(() => setLangIdx((i) => (i + 1) % pair.length), LANG_MS);
        return () => window.clearInterval(id);
    }, [pair.length]);

    const activeLang = pair[langIdx % pair.length];
    const c = dict[activeLang];

    const current = showcase[index % Math.max(1, showcase.length)];

    let sessionLink = '';
    try {
        sessionLink = API.getSessionLink();
    } catch {
        /* session not ready yet — QR fills in once it is */
    }

    const count = showcase.length || gameIDs.length || 10;

    return (
        <div className={styles.stage}>
            <div className={styles.hero} aria-hidden="true">
                {current &&
                    (current.video && !failedVideos[current.video] ? (
                        <video
                            key={current.video}
                            className={styles.shot}
                            src={current.video}
                            poster={current.poster}
                            autoPlay
                            loop
                            playsInline
                            muted
                            onError={() => setFailedVideos((f) => ({ ...f, [current.video as string]: true }))}
                            ref={(el) => {
                                // React's `muted` prop doesn't always stick (known bug) — force it
                                // so autoplay is allowed and the attract screen stays silent.
                                // eslint-disable-next-line no-param-reassign
                                if (el) el.muted = true;
                            }}
                        />
                    ) : (
                        <div
                            key={current.poster}
                            className={styles.shot}
                            style={{ backgroundImage: `url(${current.poster})` }}
                        />
                    ))}
            </div>
            <div className={styles.veil} aria-hidden="true" />
            <div className={styles.scanline} aria-hidden="true" />

            <img className={styles.logo} src="/assets/images/logo-unboared-sm-tr.png" alt="Unboared" />

            <div className={styles.langPill} aria-hidden="true">
                {pair.map((l) => (
                    <span key={l} className={l === activeLang ? styles.langOn : styles.langOff}>
                        {dict[l].label}
                    </span>
                ))}
            </div>

            <div className={styles.content}>
                <div className={styles.left} key={activeLang}>
                    <h1 className={styles.title}>
                        {c.title1}
                        <br />
                        <span className={styles.glow}>{c.title2}</span>
                    </h1>
                    <p className={styles.lead}>{c.lead}</p>
                    <div className={styles.marquee}>
                        <span className={`${styles.chip} ${styles.chipOn}`}>{c.steps[0]}</span>
                        <span className={styles.chip}>{c.steps[1]}</span>
                        <span className={styles.chip}>{c.steps[2]}</span>
                    </div>
                </div>
                <div className={styles.right}>
                    <div className={styles.qrcard}>
                        <div className={styles.qr}>
                            {sessionLink && <QRCodeSVG className={styles.qrcode} value={sessionLink} />}
                        </div>
                        <div className={styles.ctaList}>
                            {pair.map((l, i) => (
                                <div key={l} className={i === 0 ? styles.ctaPrimary : styles.ctaSecondary}>
                                    {dict[l].cta}
                                </div>
                            ))}
                        </div>
                        {/* Airports only. In a bar the animator is right there telling
                            people to scan, so the "is it free / do I need an app?"
                            objection barely exists — and showing this on every bar TV
                            would be a visible change to clients who asked for nothing.
                            Copy exists in CONTENT_ANIM too: drop this guard to enable. */}
                        {variant === 'auto' && (
                            <div className={styles.freeList}>
                                {pair.map((l) => (
                                    <div key={l} className={styles.freeBadge}>
                                        {dict[l].free}
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className={styles.sub}>{c.count(count)}</div>
                    </div>
                </div>
            </div>

            {current && (
                <div className={styles.nowShowing} key={`${current.name}-${activeLang}`}>
                    <span className={styles.nowDot} /> {c.showing} · {current.name}
                </div>
            )}
        </div>
    );
}
